// SpanishHint — gentle helper card pointing Spanish speakers to the EN | ES
// switcher in the top-right nav. Hides automatically when site is in Spanish
// mode (googtrans cookie set to es). Dismissible (persists via localStorage).
// After 10s of idle, the pointer arrow pulses stronger to grab attention.
import { useEffect, useState } from "react";
import { X, ArrowUp } from "lucide-react";

const RED  = "#E11D2A";
const NAVY = "#07111F";
const STORAGE_KEY = "bestrx_es_hint_dismissed";

function readLang() {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/googtrans=([^;]+)/);
  if (!m) return "en";
  const parts = decodeURIComponent(m[1]).split("/");
  return parts[2] || "en";
}

function switchToES() {
  if (typeof document === "undefined") return;
  document.cookie = "googtrans=/en/es; path=/; max-age=31536000";
  const host = window.location.hostname;
  if (host.split(".").length > 1) {
    const parent = "." + host.split(".").slice(-2).join(".");
    document.cookie = `googtrans=/en/es; path=/; domain=${parent}; max-age=31536000`;
  }
  window.location.reload();
}

export default function SpanishHint() {
  const [visible, setVisible] = useState(false);
  const [intense, setIntense] = useState(false);

  // Mount: decide whether to show. Show ONLY if (a) lang is English, (b) not
  // previously dismissed in this browser, (c) after a short delay to avoid
  // competing with the hero entrance animation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (readLang() === "es") return;                       // already Spanish
    if (localStorage.getItem(STORAGE_KEY) === "1") return; // user dismissed
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // After visible: ramp pulse intensity after 10s of idle.
  useEffect(() => {
    if (!visible) return;
    let idleTimer = setTimeout(() => setIntense(true), 10000);
    const resetIdle = () => {
      setIntense(false);
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setIntense(true), 10000);
    };
    window.addEventListener("scroll", resetIdle, { passive: true });
    window.addEventListener("click", resetIdle);
    window.addEventListener("touchstart", resetIdle, { passive: true });
    return () => {
      clearTimeout(idleTimer);
      window.removeEventListener("scroll", resetIdle);
      window.removeEventListener("click", resetIdle);
      window.removeEventListener("touchstart", resetIdle);
    };
  }, [visible]);

  const dismiss = (e) => {
    e?.stopPropagation();
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_e) { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <>
      {/* ─────── DESKTOP CALLOUT (floats below top-right language switcher) ─────── */}
      <aside
        data-testid="spanish-hint-desktop"
        className="hidden lg:block fixed z-[150] right-6 top-[140px] w-[300px] animate-[esHintFadeIn_500ms_ease-out_both]"
        role="complementary"
        aria-label="Spanish language helper"
      >
        {/* Pointer arrow toward the EN|ES switcher (positioned in red nav, top-right) */}
        <ArrowUp
          size={36}
          color={RED}
          strokeWidth={3}
          className={"absolute -top-9 right-8 " + (intense ? "es-hint-arrow-intense" : "es-hint-arrow")}
          aria-hidden
        />
        <div className="relative bg-white rounded-lg shadow-2xl overflow-hidden border-2"
          style={{ borderColor: RED, boxShadow: "0 22px 50px rgba(225,29,42,0.28), 0 0 0 1px rgba(7,17,31,0.05)" }}>
          <button
            onClick={dismiss}
            aria-label="Cerrar"
            data-testid="spanish-hint-close"
            className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center text-black/40 hover:text-black hover:bg-black/5"
          >
            <X size={14} />
          </button>
          <div className="px-5 pt-6 pb-3">
            <h3 className="font-heavy leading-[1.0] tracking-tight" style={{ color: RED, fontSize: "26px" }}>
              ¿NECESITA AYUDA<br/>EN ESPAÑOL?
            </h3>
            <p className="mt-3 text-[12.5px] leading-snug" style={{ color: NAVY }}>
              Todo nuestro proceso está disponible en <strong>español</strong>.
            </p>
            <ul className="mt-2.5 space-y-1 text-[11.5px]" style={{ color: NAVY }}>
              <li>✓ Evaluación en español</li>
              <li>✓ Medicamentos en español</li>
              <li>✓ Soporte en español</li>
            </ul>
          </div>
          <button
            onClick={switchToES}
            data-testid="spanish-hint-cta"
            className="w-full px-4 py-3.5 font-heavy text-white text-[12.5px] tracking-[0.14em] hover:brightness-110 transition-all"
            style={{ background: RED }}
          >
            HAGA CLIC EN ES ARRIBA<br/>
            <span className="text-[10px] font-extrabold tracking-[0.18em] text-white/85">PARA CONTINUAR.</span>
          </button>
        </div>
      </aside>

      {/* ─────── MOBILE BANNER (compact strip below header) ─────── */}
      <div
        data-testid="spanish-hint-mobile"
        className="lg:hidden sticky top-0 z-[140] bg-white border-b-2 animate-[esHintFadeIn_400ms_ease-out_both]"
        style={{ borderColor: RED, boxShadow: "0 4px 12px rgba(225,29,42,0.18)" }}
        role="complementary"
      >
        <div className="px-3 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <ArrowUp size={16} color={RED} strokeWidth={3} className={intense ? "es-hint-arrow-intense" : "es-hint-arrow"} aria-hidden />
            <div className="min-w-0">
              <p className="font-heavy leading-tight text-[12.5px]" style={{ color: RED }}>¿NECESITA AYUDA EN ESPAÑOL?</p>
              <p className="text-[10.5px] leading-tight" style={{ color: NAVY }}>Todo está disponible en español.</p>
            </div>
          </div>
          <button
            onClick={switchToES}
            data-testid="spanish-hint-mobile-cta"
            className="px-3 py-2 font-heavy text-white text-[10.5px] tracking-[0.12em] whitespace-nowrap"
            style={{ background: RED }}
          >
            CAMBIAR A ES →
          </button>
          <button
            onClick={dismiss}
            aria-label="Cerrar"
            data-testid="spanish-hint-mobile-close"
            className="w-7 h-7 rounded-full inline-flex items-center justify-center text-black/40 hover:text-black"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </>
  );
}
